import React, { useState } from "react";
import "../styles/StudentRegistration.css";
import { registerBook } from "../api/BookDetails";
import validateUser from "../utility/uservalidation";
import Loader from "../utility/FullPageLoader";
import {
  BookRegistrationData,
  FormErrors,
} from "../interfaces/BookInterface";
import { LoadingModel } from "../interfaces/LoadingInterface";
import CustomDatePicker from "./CustomDatePicker";

const BookRegistration: React.FC = () => {
  const [formData, setFormData] = useState<BookRegistrationData>({
    ISBN: "",
    BookTitle: "",
    Author: "",
    RFID: "",
    Price: 0,
    AvailableCopies: 0,
     });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null); // ✅ Add this
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user"); // 'user' = front, 'environment' = back
  const [regLoading, setRegLoading] = useState<LoadingModel>({
    isLoading: false,
    message: "",
  });

  const validateForm = async (): Promise<boolean> => {
    const newErrors: FormErrors = {};

    // Required field validation
    if (!formData.ISBN.trim()) {
      newErrors.ISBN = "ISBN is required";
    }

    if (!formData.BookTitle.trim()) {
      newErrors.BookTitle = "BookTitle is required";
    }

     if (!formData.Author.trim()) {
      newErrors.Author = "Author is required";
    }

     if (!formData.RFID.trim()) {
      newErrors.RFID = "RFID is required";
    }

      if (!formData.Price) {
      newErrors.Price =0;
    }

      if (!formData.AvailableCopies) {
      newErrors.AvailableCopies = 0;
    }






    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        photo: "Please select a valid image file",
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB max
      setErrors((prev) => ({
        ...prev,
        photo: "Image size must be less than 5MB",
      }));
      return;
    }

    // ✅ Save real file (for backend)
    setFormData((prev) => ({ ...prev, photo: file }));
    setErrors((prev) => ({ ...prev, photo: "" }));

    // ✅ Generate preview
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const startCamera = async (mode?: "user" | "environment") => {
    try {
      // Stop existing stream if any
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }

      const cameraMode = mode || facingMode;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: cameraMode,
        },
      });
      setCameraStream(stream);
      setFacingMode(cameraMode);
      setShowCamera(true);
    } catch (error) {
      console.error("Error accessing camera:", error);
      setErrors((prev) => ({
        ...prev,
        photo: "Unable to access camera. Please check permissions.",
      }));
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const switchCamera = async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    await startCamera(newMode);
  };
  // Ensure your state type is: photo: File | null
  // setFormData: React.Dispatch<React.SetStateAction<StudentRegistrationData>>

  const capturePhoto = async () => {
    const video = document.getElementById(
      "camera-video"
    ) as HTMLVideoElement | null;
    if (!video) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // make sure video has dimensions
    const width = video.videoWidth || 0;
    const height = video.videoHeight || 0;
    if (!width || !height) return;

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(video, 0, 0, width, height);

    // Generate preview data URL for display
    const previewDataURL = canvas.toDataURL("image/jpeg", 0.8);
    setPhotoPreview(previewDataURL);

    // Prefer toBlob (smaller memory than dataURL)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.8)
    );

    if (!blob) {
      // Fallback if toBlob not supported (very rare)
      const dataURL = canvas.toDataURL("image/jpeg", 0.8);
      const fallbackBlob = dataUrlToBlob(dataURL);
      if (!fallbackBlob) return;
      const fallbackFile = new File([fallbackBlob], makePhotoName(), {
        type: "image/jpeg",
      });
      setFormData((prev) => ({ ...prev, photo: fallbackFile }));
      setErrors((prev) => ({ ...prev, photo: "" }));
      stopCamera();
      return;
    }

    const file = new File([blob], makePhotoName(), { type: "image/jpeg" });

    setFormData((prev) => ({ ...prev, photo: file })); // <-- File, not string
    setErrors((prev) => ({ ...prev, photo: "" }));
    stopCamera();
  };

  // Helper: generate a filename
  const makePhotoName = () => `capture_${Date.now()}.jpg`;

  // Helper: convert dataURL -> Blob (fallback path)
  function dataUrlToBlob(dataURL: string): Blob | null {
    const parts = dataURL.split(",");
    if (parts.length !== 2) return null;
    const mimeMatch = parts[0].match(/data:(.*?);base64/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const byteString = atob(parts[1]);
    const len = byteString.length;
    const u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = byteString.charCodeAt(i);
    return new Blob([u8], { type: mime });
  }
  const removePhoto = () => {
    setFormData((prev) => ({ ...prev, photo: null }));
    setPhotoPreview(null);
    setErrors((prev) => ({ ...prev, photo: "" }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // Prepare the value we will save (default = raw value)
    let newValue = value;

    // Format Aadhar number as user types (XXXX XXXX XXXX) - max 12 digits
    if (name === "aadharNumber") {
      newValue = value
        .replace(/\D/g, "") // remove non-digits
        .slice(0, 12) // limit to 12 digits
        .replace(/(\d{4})(?=\d)/g, "$1 "); // insert space after every 4 digits
    }

    // Format Mobile Number as user types (XXXXX XXXXX) - max 10 digits
    else if (
      name === "alternateNumber" ||
      name === "mobileNumber" ||
      name === "guardianMobileNumber"
    ) {
      newValue = value
        .replace(/\D/g, "") // remove non-digits
        .slice(0, 10); // limit to 10 digits
    }

    // Set form data once
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // avoid double submit
    if (isSubmitting) return;

    // 1) validate first
    const isValid = await validateForm();
    setRegLoading({ isLoading: true, message: "Validating form data..." });
    if (!isValid) {
      setRegLoading({ isLoading: false, message: "" });
      return;
    }

    // 2) only after validation passes, submit
    setIsSubmitting(true);
    setErrors({});

    try {
      console.log("Submitting form data:", formData);
      setRegLoading({ isLoading: true, message: "Submitting form data..." });
      // real call:
      await registerBook(formData);

      // or demo delay:
      // await new Promise((resolve) => setTimeout(resolve, 1500));

      // 3) reset on success
      setFormData({
        ISBN: "",
        BookTitle: "",
        Author: "",
        RFID: "",
        Price: 0,
        AvailableCopies: 0,
       
      });
      setPhotoPreview(null);
      setIsSubmitted(true);

      // auto-hide success
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({
        general: "Failed to register student. Please try again." + error,
      });
    } finally {
      setIsSubmitting(false);
      setRegLoading({ isLoading: false, message: "" });
    }
  };

  const resetForm = () => {
    setFormData({
        ISBN: "",
        BookTitle: "",
        Author: "",
        RFID: "",
        Price: 0,
        AvailableCopies: 0,
    });
    setErrors({});
    setIsSubmitted(false);
    setPhotoPreview(null);
    stopCamera();
  };

  return (
    <div className="student-registration">
      {regLoading.isLoading && <Loader message={regLoading.message} />}
      <div className="registration-header">
        <h2>Book Registration</h2>
        <p>Register new Books in the library management system</p>
      </div>

      <div className="registration-card">
        {isSubmitted && (
          <div className="success-banner">
            <div className="success-icon">✅</div>
            <div className="success-content">
              <h4>Book Registered Successfully!</h4>
              <p>
                The Book has been added to the system.
              </p>
            </div>
          </div>
        )}

        {errors.general && (
          <div className="error-banner">
            <div className="error-icon">⚠️</div>
            <div className="error-content">
              <h4>Registration Failed</h4>
              <p>{errors.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-header-section">
            <div className="form-section">
              <h3>Book Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">ISBN *</label>
                  <input
                    type="text"
                    id="ISBN"
                    name="ISBN"
                    value={formData.ISBN}
                    onChange={handleInputChange}
                    className={errors.ISBN ? "error" : ""}
                    placeholder="Enter ISBN"
                  />
                  {errors.ISBN && (
                    <span className="error-message">{errors.ISBN}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="BookTitle">BookTitle *</label>
                  <input
                    type="text"
                    id="BookTitle"
                    name="BookTitle"
                    value={formData.BookTitle}
                    onChange={handleInputChange}
                    className={errors.BookTitle ? "error" : ""}
                    placeholder="Enter BookTitle"
                  />
                  {errors.BookTitle && (
                    <span className="error-message">{errors.BookTitle}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="Author">Author *</label>
                  <input
                    type="text"
                    id="Author"
                    name="Author"
                    value={formData.Author}
                    onChange={handleInputChange}
                    className={errors.Author ? "error" : ""}
                    placeholder="Enter Author"
                  />
                  {errors.Author && (
                    <span className="error-message">{errors.Author}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="RFID">RFID *</label>
                  <input
                    type="text"
                    id="RFID"
                    name="RFID"
                    value={formData.RFID}
                    onChange={handleInputChange}
                    className={errors.RFID ? "error" : ""}
                    placeholder="Enter RFID"
                  />
                  {errors.RFID && (
                    <span className="error-message">{errors.RFID}</span>
                  )}
                </div>
              </div>

            

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="aadharNumber">Price *</label>
                  <input
                    type="number"
                    id="Price"
                    name="Price"
                    value={formData.Price}
                    onChange={handleInputChange}
                    className={errors.Price ? "error" : ""}
                    placeholder="Enter Price"
                  
                  />
                  {errors.Price && (
                    <span className="error-message">{errors.Price}</span>
                  )}
                </div>

             
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="AvailableCopies">
                    AvailableCopies
                  </label>
                  <input
                    type="number"
                    id="AvailableCopies"
                    name="AvailableCopies"
                    value={formData.AvailableCopies}
                    onChange={handleInputChange}
                    className={errors.AvailableCopies ? "error" : ""}
                    placeholder="Enter Copies"
                  />
                  {errors.AvailableCopies && (
                    <span className="error-message">
                      {errors.AvailableCopies}
                    </span>
                  )}
                </div>

              </div>

         
            </div>

          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={resetForm}>
              Reset Form
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Registering Book...
                </>
              ) : (
                "Register Book"
              )}
            </button>
          </div>
        </form>
      </div>

         </div>
  );
};

export default BookRegistration;
